import { TriangleAlert } from 'lucide-react'
import { cn } from '../../lib/cn'

// Neutral by default so amber stays meaningful (design-system §6.8). Amber only
// when expressing active session state; status variants for feedback/outcome.
type BadgeVariant = 'neutral' | 'ember' | 'success' | 'warning' | 'danger' | 'info'

interface BadgeProps {
  children: React.ReactNode
  variant?: BadgeVariant
  className?: string
  /** Optional leading icon (Lucide-style line glyph). */
  icon?: React.ReactNode
}

const variantStyles: Record<BadgeVariant, string> = {
  neutral: 'bg-slate-800 text-slate-200 ring-1 ring-slate-700',
  // /60 ring: the §6.4 selection-ring opacity — /40 composites below the 3:1
  // non-text boundary line (WCAG 1.4.11) on these dark surfaces.
  ember: 'bg-ember-500/15 text-ember-300 ring-1 ring-ember-400/60',
  success: 'bg-success/12 text-success-fg ring-1 ring-success/40',
  warning: 'bg-warning-fg/12 text-warning-fg ring-1 ring-warning-fg/40',
  danger: 'bg-danger/12 text-danger-fg ring-1 ring-danger/40',
  info: 'bg-info-fg/12 text-info-fg ring-1 ring-info-fg/40',
}

export function Badge({ children, variant = 'neutral', className, icon }: BadgeProps) {
  // §2.5: warning is caution semantics and must carry a glyph so it never
  // reads as the ember accent — icon-less warning badges get the triangle.
  const resolvedIcon =
    icon ?? (variant === 'warning' ? <TriangleAlert className="h-3.5 w-3.5" strokeWidth={1.75} /> : null)

  return (
    <span
      className={cn(
        // whitespace-nowrap: a pill never wraps internally (deformed lozenge) —
        // chip ROWS wrap between pills (§7), each pill stays one line.
        'inline-flex items-center gap-1 whitespace-nowrap rounded-chip px-2.5 py-1 text-xs font-medium tracking-wide',
        variantStyles[variant],
        className,
      )}
    >
      {resolvedIcon && (
        <span className="inline-flex shrink-0" aria-hidden="true">
          {resolvedIcon}
        </span>
      )}
      {children}
    </span>
  )
}
